import React, { Fragment, useEffect, useMemo, useState } from 'react';
import useScrollPercentage from './hooks/useScrollPercentage';
import 'react-virtualized/styles.css';
import { DocumentsModalFooter } from 'components/newAccountSteps/initialStep/documentation/documentsModal/DocumentsModalFooter';
import { DocumentsCarousel } from 'components/newAccountSteps/initialStep/documentation/documentsModal/DocumentsCarousel';
import { DocumentsModalHeader } from 'components/newAccountSteps/initialStep/documentation/documentsModal/DocumentsModalHeader';
import { useDocumentTimeout } from 'components/newAccountSteps/initialStep/documentation/documentsModal/hooks/useDocumentTimeout';
import { useScrollInfoModal } from 'components/newAccountSteps/initialStep/documentation/documentsModal/hooks/useScrollInfoModal';
import {
    doTagAcceptDocumentAndContinue,
    doTagCancelDocumentsModal,
    doTagConfirmDocument,
    doTagContinueDocument,
    doTagDocumentRead,
    doTagExitDocumentsModal,
} from 'components/newAccountSteps/initialStep/documentation/tagging';
import { useScrollDisable } from './hooks/useScrollDisable';
import { isMobile, isSafari } from 'utilities/browser';

const DocumentsModal = ({ documentsList, showDocumentModal, onDocumentRead, onDocumentAcceptance, handleClose }) => {
    const [currentDocumentIndex, setCurrentDocumentIndex] = useState(0);
    const currentDocument = useMemo(() => documentsList?.[currentDocumentIndex], [documentsList, currentDocumentIndex]);
    const areAlldocumentsAccepted = useMemo(
        () => documentsList.every(document => document.isAccepted),
        [documentsList]
    );
    const isDocumentAccepted = currentDocument?.isAccepted;
    const isDocumentRead = currentDocument?.isRead;

    useEffect(() => {
        setCurrentDocumentIndex(0);

        if (showDocumentModal && isMobile() && isSafari()) {
            document.body.style.transform = 'scale(1.025)';
        }
    }, [showDocumentModal]);

    const resetBodyScale = () => {
        document.body.style.transform = '';
    };

    useScrollDisable({ condition: showDocumentModal });
    const [pdfContainerRef, scrollPercentage] = useScrollPercentage();

    const { renderScrollInfoModal, openScrollInfoModal, scrollInfoModalShowed } = useScrollInfoModal({
        onClickCancelButton: e => {
            resetBodyScale();
            handleClose(e, scrollPercentage);
        },
    });

    const { resetTimeoutFunction, stopTimeoutFunction } = useDocumentTimeout(
        {
            startCondition: !isDocumentRead && !isDocumentAccepted && showDocumentModal && !scrollInfoModalShowed,
            callback: () => {
                openScrollInfoModal();
            },
            timeLapse: 15000,
        },
        [showDocumentModal]
    );

    const handleScroll = ({ documentIndex, isAtBottom }) => {
        const document = documentsList[documentIndex];
        if (!isAtBottom) {
            resetTimeoutFunction();
        } else if (!document.isRead) {
            onDocumentRead(document);
            stopTimeoutFunction();
            doTagDocumentRead(document);
        }
    };

    const handleConfirm = e => {
        e.preventDefault();
        resetBodyScale();
        handleClose(e, scrollPercentage);
        doTagConfirmDocument(currentDocument);
    };

    const handleOnContinue = e => {
        e.preventDefault();
        setCurrentDocumentIndex(prevState => prevState + 1);
        doTagContinueDocument(currentDocument);
    };

    const handleAcceptanceAndContinue = e => {
        e.preventDefault();
        onDocumentAcceptance(currentDocument);
        const nextDocumentIndex = currentDocumentIndex + 1;
        if (nextDocumentIndex === documentsList.length) {
            resetBodyScale();
            handleClose(e, scrollPercentage);
        } else {
            setCurrentDocumentIndex(nextDocumentIndex);
        }
        doTagAcceptDocumentAndContinue(currentDocument);
    };

    const handleClickContinueDisabled = e => {
        e.preventDefault();
        stopTimeoutFunction();
        openScrollInfoModal();
    };

    const handleClickCloseButton = e => {
        e.preventDefault();
        resetBodyScale();
        handleClose(e, scrollPercentage);
        doTagExitDocumentsModal(currentDocument);
    };

    const handleClickCancelButton = e => {
        e.preventDefault();
        openScrollInfoModal();
        stopTimeoutFunction();
        doTagCancelDocumentsModal(currentDocument);
    };

    return (
        <Fragment>
            {showDocumentModal && (
                <div className="documents__modal__box-outside modal-open">
                    <div className="documents__modal__box-inside">
                        <div className="documents__modal__container">
                            <DocumentsModalHeader
                                documentsList={documentsList}
                                currentDocumentIndex={currentDocumentIndex}
                            />
                            <DocumentsCarousel
                                documentsList={documentsList}
                                currentDocumentIndex={currentDocumentIndex}
                                pdfContainerRef={pdfContainerRef}
                                onScroll={handleScroll}
                            />
                            <DocumentsModalFooter
                                areAlldocumentsAccepted={areAlldocumentsAccepted}
                                isCurrentDocumentAcepted={isDocumentAccepted}
                                isCurrentDocumentRead={isDocumentRead}
                                onClose={handleClickCloseButton}
                                onCancel={handleClickCancelButton}
                                onConfirm={handleConfirm}
                                onContinue={handleOnContinue}
                                onAcceptAndContinue={handleAcceptanceAndContinue}
                                onContinueDisabled={handleClickContinueDisabled}
                            />
                        </div>
                    </div>
                    {renderScrollInfoModal()}
                </div>
            )}
        </Fragment>
    );
};
export default DocumentsModal;
